import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { TenantConfig, TenantConfigService } from "../../../../types/tenantConfig";
import {
  resolveDirectBooking,
  useActiveProductCategory,
} from "../../../../util/categoryConfig";
import { useIsCoveredBooking } from "./useCoveredBooking";
import { spaceOf, UseCase } from "../../../../util/useCases";

/**
 * Wire 2 (service-configuration plan): does this use case run as a prepaid
 * direct booking, or as a reservation request?
 *
 * The single reader of the tenant's per-use-case `directBooking`
 * configuration. One flow per use case, never an end-user choice (PO,
 * 2026-08-21): the value selects WHICH flow the page runs — it never renders
 * a chooser.
 *
 * A use case or category the tenant never configured runs the use-case
 * description's `space.directBookingDefault` (it replaced the
 * `LEGACY_DIRECT_BOOKING` map, W0-1). Since the PO decision of 2026-10-19
 * (ADR-0026 amendment 2026-10-19) that is `false` — a reservation request —
 * for EVERY space use case, apartments included: direct booking happens only
 * once the tenant switches it on, which the admin UI allows on a chargeable
 * offer only with an immediate payment means. The admin form's seed
 * (`ServiceConfigurationSection.tsx`) and both `paymentArrangement.ts` copies
 * (`=== true`) give the same answer. It is also the answer while the config is
 * still loading.
 *
 * A use case without space properties (the station and handover flows) has no
 * request flow at all; it keeps answering "direct", which only the FAQ facts
 * read for it.
 */
const legacyDirectBooking = (useCase: TenantConfigService | UseCase): boolean =>
  spaceOf(useCase)?.directBookingDefault ?? true;

/**
 * The one answer to "does this use case (or category) book directly, or send a
 * reservation request?" — configured value first, the legacy default
 * otherwise. Pure, so the FAQs (`faqFlows.ts`) ask the same question the
 * booking flow does instead of keeping a copy of the defaults.
 */
export const directBookingFor = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | UseCase,
  categoryKey?: string
): boolean => {
  const configured = resolveDirectBooking(config, useCase, categoryKey);
  if (typeof configured === "boolean") return configured;
  return legacyDirectBooking(useCase);
};

/**
 * Does THIS booking run as a direct booking with a payment step?
 *
 * The configured mode (`directBookingFor`), except for a booking covered by
 * the customer's organization (#1926, covered bookings step C): nothing is
 * paid, so there is no checkout to run. It takes the reservation-request path
 * instead — one order call, in which the server re-derives coverage from the
 * session and creates the booking confirmed and unpaid — the path a covered
 * booking already took under "Nur Reservierung". Asked here, once, so the
 * flow, the step labels and the voucher field cannot disagree about it.
 */
export const useDirectBookingFlow = (useCase: TenantConfigService | UseCase): boolean => {
  // ADR-0026 §B: the product category is the finer configuration key and the
  // use case is its fallback. Resolved INSIDE the hook rather than pushed onto
  // callers, so every consumer picks the category up without being edited —
  // and so there is no call site that silently keeps reading the coarse value.
  const categoryKey = useActiveProductCategory();
  const isCovered = useIsCoveredBooking();
  const configured = useSelector((state: RootState) =>
    directBookingFor(state.tenantConfig?.config, useCase, categoryKey)
  );
  return configured && !isCovered;
};

export default useDirectBookingFlow;
